// print service for generating HTML reports

import { API_CONFIG } from '../config/api.ts';
import { MenuItem } from './siteConfigService.ts';

// Extended interface to handle both string and number values from API
interface PrintTipOutEntry {
  id: string;
  employeeName: string;
  employeeCode?: string;
  position: 'Server' | 'Host' | 'Bar';
  netSales: number | string;
  bar1Percent: number | string;
  bus1_5Percent: number | string;
  expoAmount: number | string;
  totalTipOut: number | string;
  section: 'Daily' | 'Event';
  shift: string;
}

export interface PrintData {
  date: string;
  daily: {
    am: PrintTipOutEntry[];
    pm: PrintTipOutEntry[];
  };
  event: {
    event1: PrintTipOutEntry[];
    event2: PrintTipOutEntry[];
  };
}

export const printService = {
  // Generate HTML content for printing
  generatePrintHTML: (data: PrintData): string => {
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const formatCurrency = (amount: number | string) => {
      const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
      return `$${numAmount.toFixed(2)}`;
    };

    const calculateTotals = (entries: PrintTipOutEntry[]) => {
      return entries.reduce((totals, entry) => ({
        netSales: totals.netSales + (typeof entry.netSales === 'string' ? parseFloat(entry.netSales) : entry.netSales),
        bar1Percent: totals.bar1Percent + (typeof entry.bar1Percent === 'string' ? parseFloat(entry.bar1Percent) : entry.bar1Percent),
        bus1_5Percent: totals.bus1_5Percent + (typeof entry.bus1_5Percent === 'string' ? parseFloat(entry.bus1_5Percent) : entry.bus1_5Percent),
        expoAmount: totals.expoAmount + (typeof entry.expoAmount === 'string' ? parseFloat(entry.expoAmount) : entry.expoAmount),
        totalTipOut: totals.totalTipOut + (typeof entry.totalTipOut === 'string' ? parseFloat(entry.totalTipOut) : entry.totalTipOut),
      }), {
        netSales: 0,
        bar1Percent: 0,
        bus1_5Percent: 0,
        expoAmount: 0,
        totalTipOut: 0,
      });
    };

    const amTotals = calculateTotals(data.daily.am);
    const pmTotals = calculateTotals(data.daily.pm);
    const event1Totals = calculateTotals(data.event.event1);
    const event2Totals = calculateTotals(data.event.event2);

    const dailyCombinedTotals = {
      netSales: amTotals.netSales + pmTotals.netSales,
      bar1Percent: amTotals.bar1Percent + pmTotals.bar1Percent,
      bus1_5Percent: amTotals.bus1_5Percent + pmTotals.bus1_5Percent,
      expoAmount: amTotals.expoAmount + pmTotals.expoAmount,
      totalTipOut: amTotals.totalTipOut + pmTotals.totalTipOut,
    };

    const eventCombinedTotals = {
      netSales: event1Totals.netSales + event2Totals.netSales,
      bar1Percent: event1Totals.bar1Percent + event2Totals.bar1Percent,
      bus1_5Percent: event1Totals.bus1_5Percent + event2Totals.bus1_5Percent,
      expoAmount: event1Totals.expoAmount + event2Totals.expoAmount,
      totalTipOut: event1Totals.totalTipOut + event2Totals.totalTipOut,
    };

    const renderTable = (entries: PrintTipOutEntry[], title: string) => {
      // Remove empty rows where all numeric fields are 0/blank and no employee name
      const cleaned = entries.filter((e) => {
        const ns = typeof e.netSales === 'string' ? parseFloat(e.netSales) : e.netSales || 0;
        const bar = typeof e.bar1Percent === 'string' ? parseFloat(e.bar1Percent) : e.bar1Percent || 0;
        const bus = typeof e.bus1_5Percent === 'string' ? parseFloat(e.bus1_5Percent) : e.bus1_5Percent || 0;
        const expo = typeof e.expoAmount === 'string' ? parseFloat(e.expoAmount) : e.expoAmount || 0;
        const total = typeof e.totalTipOut === 'string' ? parseFloat(e.totalTipOut) : e.totalTipOut || 0;
        const hasName = Boolean((e.employeeName || '').trim());
        return hasName || ns !== 0 || bar !== 0 || bus !== 0 || expo !== 0 || total !== 0;
      });

      if (cleaned.length === 0) {
        return `
          <div class="table-section">
            <h3>${title}</h3>
            <p class="no-data">No data available</p>
          </div>
        `;
      }

      const totals = calculateTotals(cleaned);
      
      return `
        <div class="table-section">
          <h3>${title}</h3>
          <table class="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Position</th>
                <th>Net Sales</th>
                <th>Bar 1%</th>
                <th>Bus 1.5%</th>
                <th>Expo</th>
                <th>Total Tip Out</th>
              </tr>
            </thead>
            <tbody>
              ${cleaned.map(entry => `
                <tr>
                  <td>${entry.employeeName} (${entry.employeeCode ?? '0'})</td>
                  <td>${entry.position}</td>
                  <td class="number">${(typeof entry.netSales === 'string' ? parseFloat(entry.netSales) : entry.netSales).toFixed(0)}</td>
                  <td class="number">${formatCurrency(entry.bar1Percent)}</td>
                  <td class="number">${formatCurrency(entry.bus1_5Percent)}</td>
                  <td class="number">${formatCurrency(entry.expoAmount)}</td>
                  <td class="number total">${formatCurrency(entry.totalTipOut)}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="2"><strong>TOTAL</strong></td>
                <td class="number"><strong>${totals.netSales.toFixed(0)}</strong></td>
                <td class="number"><strong>${formatCurrency(totals.bar1Percent)}</strong></td>
                <td class="number"><strong>${formatCurrency(totals.bus1_5Percent)}</strong></td>
                <td class="number"><strong>${formatCurrency(totals.expoAmount)}</strong></td>
                <td class="number total"><strong>${formatCurrency(totals.totalTipOut)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
    };

    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Tip Out Report - ${formatDate(data.date)}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            background: white;
            padding: 20px;
          }
          
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #1976d2;
            padding-bottom: 20px;
          }
          
          .header h1 {
            color: #1976d2;
            font-size: 28px;
            margin-bottom: 10px;
          }
          
          .header .date {
            font-size: 18px;
            color: #666;
          }
          
          .table-section {
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          
          .table-section h3 {
            color: #1976d2;
            font-size: 20px;
            margin-bottom: 15px;
            padding: 10px;
            background-color: #f5f5f5;
            border-left: 4px solid #1976d2;
          }
          
          .data-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 14px;
          }
          
          .data-table th,
          .data-table td {
            padding: 12px 8px;
            text-align: left;
            border: 1px solid #ddd;
          }
          
          .data-table th {
            background-color: #1976d2;
            color: white;
            font-weight: bold;
            text-align: center;
          }
          
          .data-table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          
          .data-table tr:hover {
            background-color: #f5f5f5;
          }
          
          .number {
            text-align: right;
          }
          
          .total {
            font-weight: bold;
            color: #1976d2;
          }
          
          .total-row {
            background-color: #e3f2fd !important;
            font-weight: bold;
          }
          
          .total-row td {
            border-top: 2px solid #1976d2;
          }
          
          .no-data {
            text-align: center;
            color: #666;
            font-style: italic;
            padding: 20px;
          }
          
          .combined-total {
            background-color: #fff3e0;
            border: 2px solid #ff9800;
            padding: 15px;
            margin: 20px 0;
            border-radius: 5px;
          }
          
          .combined-total h4 {
            color: #ff9800;
            margin-bottom: 10px;
            text-align: center;
          }
          
          .combined-total table {
            width: 100%;
            border-collapse: collapse;
          }
          
          .combined-total th,
          .combined-total td {
            padding: 8px;
            text-align: right;
            border: 1px solid #ff9800;
          }
          
          .combined-total th {
            background-color: #ff9800;
            color: white;
            text-align: center;
          }
          
          @media print {
            body {
              padding: 0;
            }
            
            .header {
              margin-bottom: 20px;
            }
            
            .table-section {
              margin-bottom: 20px;
            }
            
            .data-table {
              font-size: 12px;
            }
            
            .data-table th,
            .data-table td {
              padding: 8px 6px;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Tip Out Report</h1>
          <div class="date">${formatDate(data.date)}</div>
        </div>
        
        <div class="daily-section">
          <h2 style="color: #1976d2; margin-bottom: 20px;">Daily Shifts</h2>
          
          ${renderTable(data.daily.am, 'AM Shift')}
          ${renderTable(data.daily.pm, 'PM Shift')}
          
          <div class="combined-total">
            <h4>DAILY TOTAL (AM + PM)</h4>
            <table>
              <thead>
                <tr>
                  <th>Net Sales</th>
                  <th>Bar 1%</th>
                  <th>Bus 1.5%</th>
                  <th>Expo</th>
                  <th>Total Tip Out</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${dailyCombinedTotals.netSales.toFixed(0)}</td>
                  <td>${formatCurrency(dailyCombinedTotals.bar1Percent)}</td>
                  <td>${formatCurrency(dailyCombinedTotals.bus1_5Percent)}</td>
                  <td>${formatCurrency(dailyCombinedTotals.expoAmount)}</td>
                  <td class="total">${formatCurrency(dailyCombinedTotals.totalTipOut)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <div class="event-section">
          <h2 style="color: #1976d2; margin: 30px 0 20px 0;">Events</h2>
          
          ${renderTable(data.event.event1, 'Event 1')}
          ${renderTable(data.event.event2, 'Event 2')}
          
          <div class="combined-total">
            <h4>EVENT TOTAL (Event 1 + Event 2)</h4>
            <table>
              <thead>
                <tr>
                  <th>Net Sales</th>
                  <th>Bar 1%</th>
                  <th>Bus 1.5%</th>
                  <th>Expo</th>
                  <th>Total Tip Out</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${eventCombinedTotals.netSales.toFixed(0)}</td>
                  <td>${formatCurrency(eventCombinedTotals.bar1Percent)}</td>
                  <td>${formatCurrency(eventCombinedTotals.bus1_5Percent)}</td>
                  <td>${formatCurrency(eventCombinedTotals.expoAmount)}</td>
                  <td class="total">${formatCurrency(eventCombinedTotals.totalTipOut)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <div class="footer" style="margin-top: 40px; text-align: center; color: #666; font-size: 12px;">
          <p>Generated on ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;

    return html;
  },

  // Open print dialog in new tab
  openPrintDialog: (data: PrintData) => {
    const html = printService.generatePrintHTML(data);
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
    }
  }
};

// Balance Print Data Interface
export interface PrintBalanceData {
  date: string;
  balance: {
    am: Array<{
      employeeName: string;
      employeeCode?: string;
      type: 'server' | 'host' | 'bar' | 'manager';
      cashDue: number;
      netSales: number;
    }>;
    pm: Array<{
      employeeName: string;
      employeeCode?: string;
      type: 'server' | 'host' | 'bar' | 'manager';
      cashDue: number;
      netSales: number;
    }>;
  };
  paid_outs: Array<{
    description: string;
    cashDue: number;
  }>;
  busser_tips: Array<{
    busserName: string;
    tips: number;
  }>;
  event_servers_bartender_tips: Array<{
    eventServerBartender: string;
    pooledTips: number;
  }>;
  negative_cash_outs: Array<{
    tookFrom: string;
    negativeCashOuts: number;
  }>;
  keno_cash: Array<{
    employeeName: string;
    employeeCode?: string;
    kenoCash: number;
  }>;
}

// Balance Print Service
export const balancePrintService = {
  // Generate HTML content for balance printing
  generatePrintHTML: (data: PrintBalanceData): string => {
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const formatCurrency = (amount: number) => {
      const sign = amount < 0 ? '-' : '';
      return `${sign}$${Math.abs(amount).toFixed(2)}`;
    };

    const formatNumber = (amount: number) => {
      const sign = amount < 0 ? '-' : '';
      return `${sign}${Math.abs(amount).toFixed(2)}`;
    };

    // Calculate totals
    const amCashDue = data.balance.am.reduce((sum, row) => sum + row.cashDue, 0);
    const pmCashDue = data.balance.pm.reduce((sum, row) => sum + row.cashDue, 0);
    const paidOutsCashDue = data.paid_outs.reduce((sum, entry) => sum + entry.cashDue, 0);
    const totalCashDue = amCashDue + pmCashDue + paidOutsCashDue;

    const amNetSales = data.balance.am.reduce((sum, row) => sum + row.netSales, 0);
    const pmNetSales = data.balance.pm.reduce((sum, row) => sum + row.netSales, 0);
    const totalNetSales = amNetSales + pmNetSales;

    const renderBalanceTable = (entries: Array<{employeeName: string; employeeCode?: string; type: string; cashDue: number; netSales: number}>, title: string) => {
      if (entries.length === 0) {
        return `
          <div class="table-section">
            <h3>${title}</h3>
            <p class="no-data">No data available</p>
          </div>
        `;
      }

      // Group by type
      const servers = entries.filter(e => e.type === 'server');
      const bar = entries.filter(e => e.type === 'bar');
      const host = entries.filter(e => e.type === 'host');
      const manager = entries.filter(e => e.type === 'manager');

      const renderSection = (sectionEntries: typeof entries, sectionTitle: string) => {
        if (sectionEntries.length === 0) return '';
        return `
          <tr class="section-header">
            <td colspan="4"><strong>${sectionTitle}</strong></td>
          </tr>
          ${sectionEntries.map(entry => `
            <tr>
              <td>${entry.employeeName}${entry.employeeCode ? ` (${entry.employeeCode})` : ''}</td>
              <td class="number">${formatCurrency(entry.cashDue)}</td>
              <td class="number">${formatNumber(entry.netSales)}</td>
            </tr>
          `).join('')}
        `;
      };

      const sectionTotals = {
        cashDue: entries.reduce((sum, e) => sum + e.cashDue, 0),
        netSales: entries.reduce((sum, e) => sum + e.netSales, 0),
      };

      return `
        <div class="table-section">
          <h3>${title}</h3>
          <table class="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th class="number">Cash Due</th>
                <th class="number">Net Sales</th>
              </tr>
            </thead>
            <tbody>
              ${renderSection(servers, 'Servers')}
              ${renderSection(bar, 'Bar')}
              ${renderSection(host, 'Host')}
              ${renderSection(manager, 'Manager')}
              <tr class="total-row">
                <td><strong>TOTAL</strong></td>
                <td class="number"><strong>${formatCurrency(sectionTotals.cashDue)}</strong></td>
                <td class="number"><strong>${formatNumber(sectionTotals.netSales)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      `;
    };

    const renderSimpleTable = (entries: any[], title: string, columns: string[], getRowData: (entry: any) => string[]) => {
      if (entries.length === 0) {
        return `
          <div class="table-section">
            <h3>${title}</h3>
            <p class="no-data">No data available</p>
          </div>
        `;
      }

      return `
        <div class="table-section">
          <h3>${title}</h3>
          <table class="data-table">
            <thead>
              <tr>
                ${columns.map(col => `<th>${col}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${entries.map(entry => {
                const rowData = getRowData(entry);
                return `
                  <tr>
                    ${rowData.map((cell, idx) => 
                      idx === rowData.length - 1 && typeof cell === 'number' 
                        ? `<td class="number">${formatCurrency(cell)}</td>`
                        : `<td>${cell}</td>`
                    ).join('')}
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
      `;
    };

    const html = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Balance Report - ${formatDate(data.date)}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            background: white;
            padding: 20px;
          }
          
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #1976d2;
            padding-bottom: 20px;
          }
          
          .header h1 {
            color: #1976d2;
            font-size: 28px;
            margin-bottom: 10px;
          }
          
          .header .date {
            font-size: 18px;
            color: #666;
          }
          
          .table-section {
            margin-bottom: 30px;
            page-break-inside: avoid;
          }
          
          .table-section h3 {
            color: #1976d2;
            font-size: 20px;
            margin-bottom: 15px;
            padding: 10px;
            background-color: #f5f5f5;
            border-left: 4px solid #1976d2;
          }
          
          .data-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 14px;
          }
          
          .data-table th,
          .data-table td {
            padding: 12px 8px;
            text-align: left;
            border: 1px solid #ddd;
          }
          
          .data-table th {
            background-color: #1976d2;
            color: white;
            font-weight: bold;
            text-align: center;
          }
          
          .data-table .number {
            text-align: right;
          }
          
          .data-table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          
          .data-table .section-header {
            background-color: #e3f2fd;
            font-weight: bold;
          }
          
          .data-table .section-header td {
            padding-left: 20px;
          }
          
          .total-row {
            background-color: #e3f2fd !important;
            font-weight: bold;
          }
          
          .total-row td {
            border-top: 2px solid #1976d2;
          }
          
          .totals-section {
            background-color: #fff3e0;
            border: 2px solid #ff9800;
            padding: 15px;
            margin: 20px 0;
            border-radius: 5px;
          }
          
          .totals-section h4 {
            color: #ff9800;
            margin-bottom: 10px;
            text-align: center;
          }
          
          .totals-section table {
            width: 100%;
            border-collapse: collapse;
          }
          
          .totals-section th,
          .totals-section td {
            padding: 8px;
            text-align: right;
            border: 1px solid #ff9800;
          }
          
          .totals-section th {
            background-color: #ff9800;
            color: white;
            text-align: center;
          }
          
          .no-data {
            text-align: center;
            color: #666;
            font-style: italic;
            padding: 20px;
          }
          
          @media print {
            body {
              padding: 0;
            }
            
            .header {
              margin-bottom: 20px;
            }
            
            .table-section {
              margin-bottom: 20px;
            }
            
            .data-table {
              font-size: 12px;
            }
            
            .data-table th,
            .data-table td {
              padding: 8px 6px;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>New Balance Form</h1>
          <div class="date">${formatDate(data.date)}</div>
        </div>
        
        ${renderBalanceTable(data.balance.am, 'AM')}
        ${renderBalanceTable(data.balance.pm, 'PM')}
        
        ${renderSimpleTable(
          data.paid_outs,
          'Paid outs',
          ['Description', 'Cash Due'],
          (entry) => [entry.description, entry.cashDue]
        )}
        
        <div class="totals-section">
          <h4>Total's</h4>
          <table>
            <thead>
              <tr>
                <th>Cash Due</th>
                <th>Net Sales</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>${formatCurrency(totalCashDue)}</strong></td>
                <td><strong>${formatNumber(totalNetSales)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
        
        ${renderSimpleTable(
          data.busser_tips,
          'Busser Tips',
          ["Busser's Name", 'Tips'],
          (entry) => [entry.busserName, entry.tips]
        )}
        
        ${renderSimpleTable(
          data.event_servers_bartender_tips,
          'Event Servers/Bartenders Tips',
          ['Event Servers/Bartenders', 'Pooled Tips'],
          (entry) => [entry.eventServerBartender, entry.pooledTips]
        )}
        
        ${renderSimpleTable(
          data.negative_cash_outs,
          'Negative Cash Outs',
          ['Took from', 'Negative Cash Outs'],
          (entry) => [entry.tookFrom, entry.negativeCashOuts]
        )}
        
        ${renderSimpleTable(
          data.keno_cash,
          'Keno Cash',
          ['Employee', 'Keno Cash'],
          (entry) => [`${entry.employeeName}${entry.employeeCode ? ` (${entry.employeeCode})` : ''}`, entry.kenoCash]
        )}
        
        <div class="footer" style="margin-top: 40px; text-align: center; color: #666; font-size: 12px;">
          <p>Generated on ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;

    return html;
  },

  // Open print dialog for balance data
  openPrintDialog: (data: PrintBalanceData) => {
    try {
      const html = balancePrintService.generatePrintHTML(data);
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        throw new Error('Failed to open print window. Please allow popups for this site.');
      }
      
      printWindow.document.write(html);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 500);
      };
      
      // Fallback if onload doesn't fire
      setTimeout(() => {
        if (printWindow.document.readyState === 'complete') {
          printWindow.print();
        }
      }, 1000);
    } catch (error) {
      console.error('Error opening print dialog:', error);
      throw error;
    }
  }
};

// Daily Specials Print Service
// Category-tagged items each print as their own single section. Appetizers and Soup
// print above the day-grouped Entrées; Promo and Desserts print below it.
const CATEGORY_SECTIONS_BEFORE: { key: string; label: string }[] = [
  { key: 'appetizer', label: 'Appetizers' },
  { key: 'soup', label: 'Soup' },
];
const CATEGORY_SECTIONS_AFTER: { key: string; label: string }[] = [
  { key: 'promo', label: 'Promo' },
  { key: 'dessert', label: 'Desserts' },
];
const ALL_CATEGORY_SECTIONS = [...CATEGORY_SECTIONS_BEFORE, ...CATEGORY_SECTIONS_AFTER];

const buildCategoryGroups = (items: MenuItem[], sections: { key: string; label: string }[]) =>
  sections
    .map(({ key, label }) => ({ day: label, items: items.filter((i) => i.category === key) }))
    .filter((group) => group.items.length > 0);

// Splits items into the leading category sections, a single Entrées section (no
// per-day grouping/header - every entree prints together under one "Entrées" banner),
// and the trailing category sections, in that print order.
const groupItemsForPrint = (items: MenuItem[]): { day: string | null; items: MenuItem[] }[] => {
  const categoryKeys = ALL_CATEGORY_SECTIONS.map((s) => s.key);
  const entreeItems = items.filter((i) => !categoryKeys.includes(i.category || ''));
  const entreeGroups = entreeItems.length > 0 ? [{ day: 'Entrées', items: entreeItems }] : [];

  return [
    ...buildCategoryGroups(items, CATEGORY_SECTIONS_BEFORE),
    ...entreeGroups,
    ...buildCategoryGroups(items, CATEGORY_SECTIONS_AFTER),
  ];
};

// Promo and Desserts always print on their own second page - printed with the exact
// same header/frame/footer template as page 1 - never alongside Appetizers, Soup, or
// Entrées, regardless of item counts. No second page is created if there simply are
// no Promo/Desserts items to put on one.
const paginateGroups = (
  groups: { day: string | null; items: MenuItem[] }[]
): { day: string | null; items: MenuItem[] }[][] => {
  if (groups.length === 0) return [[]];

  const movableLabels = CATEGORY_SECTIONS_AFTER.map((s) => s.label); // Promo, Desserts
  const page1Groups = groups.filter((g) => !movableLabels.includes(g.day || ''));
  const page2Groups = groups.filter((g) => movableLabels.includes(g.day || ''));

  if (page2Groups.length === 0) return [page1Groups];
  if (page1Groups.length === 0) return [page2Groups];
  return [page1Groups, page2Groups];
};

export const dailySpecialsPrintService = {
  // Generate an attractive, Letter-size (8.5in x 11in) printable specials board.
  // Food specials and desserts are combined onto a single page, desserts as a closing section.
  generatePrintHTML: (items: MenuItem[]): string => {
    const allGroups = groupItemsForPrint(items);
    const logoUrl = `${API_CONFIG.SITE_URL}/assets/images/logo.png`;

    const renderItem = (item: MenuItem) => `
      <div class="special-item">
        <div class="special-item-row">
          <span class="special-name">${item.name}</span>
          <span class="special-dots"></span>
          <span class="special-price">${item.price}</span>
        </div>
        ${item.description ? `<div class="special-desc">${item.description}</div>` : ''}
      </div>
    `;

    const renderGroup = (group: { day: string | null; items: MenuItem[] }) => `
      <div class="day-group">
        ${group.day ? `<div class="day-banner"><span>${group.day}</span></div>` : ''}
        ${group.items.map(renderItem).join('')}
      </div>
    `;

    const renderPage = (opts: { title: string; subtitle: string; groups: { day: string | null; items: MenuItem[] }[]; emptyMessage: string }) => `
      <div class="page">
        <div class="frame">
          <span class="corner-ornament corner-tl">&#10087;</span>
          <span class="corner-ornament corner-tr">&#10087;</span>
          <span class="corner-ornament corner-bl">&#10087;</span>
          <span class="corner-ornament corner-br">&#10087;</span>
          <div class="header">
            <div class="logo-badge"><img src="${logoUrl}" alt="Post Office Pub" onerror="this.parentElement.style.display='none'" /></div>
            <div class="header-text">
              <h1>${opts.title}</h1>
              <div class="subtitle">${opts.subtitle}</div>
            </div>
          </div>
          <div class="ornament">&#10022; &#10022; &#10022;</div>
          <div class="specials-list">
            ${opts.groups.length === 0 ? `<div class="empty-state">${opts.emptyMessage}</div>` : opts.groups.map((group) => {
              // A dark divider line after every section (Appetizers, Soup, Entrées,
              // Promo, Desserts), including after the last one before the footer.
              return renderGroup(group) + '<div class="section-divider"></div>';
            }).join('')}
          </div>
          <div class="footer">
            <div class="ornament">&#10022; &#10022; &#10022;</div>
            <div class="footer-note">Specials subject to availability &middot; Prices subject to change &middot; Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
            <div class="allergy-disclaimer"><strong>Allergy Notice:</strong> Please inform your server of any food allergies or dietary restrictions before ordering.</div>
          </div>
        </div>
      </div>
    `;

    const pageGroupChunks = paginateGroups(allGroups);
    const pages = pageGroupChunks.map((groups) =>
      renderPage({
        title: 'Daily Specials',
        subtitle: 'Post Office Pub &middot; North Grafton, MA',
        groups,
        emptyMessage: 'No specials added yet.',
      })
    );

    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Daily Specials</title>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Montserrat:wght@400;500;600&display=swap" rel="stylesheet">
        <style>
          @page {
            size: letter;
            margin: 0;
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Montserrat', Arial, sans-serif;
            background: #ffffff;
            color: #262626;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .page {
            width: 8.5in;
            height: 11in;
            padding: 0.4in;
            page-break-after: always;
          }
          .page:last-of-type {
            page-break-after: auto;
          }
          .frame {
            position: relative;
            height: 100%;
            border: 8px double #000000;
            outline: 1px solid #000000;
            outline-offset: -14px;
            border-radius: 10px;
            padding: 0.2in 0.55in 0.4in;
            display: flex;
            flex-direction: column;
          }
          .corner-ornament {
            position: absolute;
            font-size: 24px;
            color: #b71c1c;
            line-height: 1;
          }
          .corner-tl { top: 10px; left: 10px; }
          .corner-tr { top: 10px; right: 10px; transform: scaleX(-1); }
          .corner-bl { bottom: 10px; left: 10px; transform: scaleY(-1); }
          .corner-br { bottom: 10px; right: 10px; transform: scale(-1, -1); }
          .header {
            position: relative;
            min-height: 110px;
            margin-top: -0.1in;
            margin-bottom: 0.04in;
          }
          .header .logo-badge {
            position: absolute;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            width: 110px;
            height: 110px;
            padding: 20px;
            box-sizing: border-box;
            border-radius: 50%;
            background: #ffffff;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
          }
          .header .logo-badge img { width: 100%; height: 100%; object-fit: contain; }
          .header .header-text {
            text-align: center;
            display: flex;
            flex-direction: column;
            justify-content: center;
            min-height: 110px;
          }
          .header h1 {
            font-family: 'Playfair Display', Georgia, serif;
            font-weight: 900;
            font-size: 28px;
            letter-spacing: 2px;
            color: #7f0000;
            line-height: 1.1;
          }
          .header .subtitle {
            font-size: 11px;
            letter-spacing: 2px;
            text-transform: uppercase;
            color: #b71c1c;
            margin-top: 3px;
          }
          .ornament {
            text-align: center;
            color: #b71c1c;
            font-size: 14px;
            letter-spacing: 8px;
            margin: 6px 0;
          }
          /* Not flex-grown and no overflow clipping - sized to its actual content so the
             footer below always follows it in normal flow instead of risking an overlap
             when a page's content runs long. */
          .specials-list { overflow: visible; }
          .section-divider {
            border-top: 6px solid #000000;
            margin: 10px -0.56in 14px;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          /* Keep a whole section together across a page break instead of splitting it -
             if it doesn't fit on the current page, the entire section moves to the next. */
          .day-group {
            margin-bottom: 6px;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .day-banner {
            text-align: center;
            margin: 2px 0 6px;
          }
          .day-banner span {
            display: inline-block;
            color: #b71c1c;
            font-family: 'Playfair Display', Georgia, serif;
            font-weight: 900;
            font-size: 15px;
            letter-spacing: 2px;
            text-transform: uppercase;
          }
          .special-item { margin: 0 4px 6px; break-inside: avoid; page-break-inside: avoid; }
          .special-item-row {
            display: flex;
            align-items: baseline;
            gap: 8px;
          }
          .special-name {
            font-family: 'Playfair Display', Georgia, serif;
            font-weight: 700;
            font-size: 15px;
            color: #262626;
            white-space: nowrap;
          }
          .special-dots {
            flex: 1;
            border-bottom: 2px dotted #b0a89e;
            transform: translateY(-4px);
          }
          .special-price {
            font-family: 'Playfair Display', Georgia, serif;
            font-weight: 700;
            font-size: 15px;
            color: #b71c1c;
            white-space: nowrap;
          }
          .special-desc {
            font-size: 10px;
            font-style: italic;
            color: #6b6b6b;
            margin-top: 1px;
          }
          .footer {
            text-align: center;
            margin-top: 14px;
            padding-top: 6px;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .footer-note {
            font-size: 10.5px;
            color: #8a8a8a;
            letter-spacing: 0.5px;
          }
          .allergy-disclaimer {
            font-size: 9px;
            color: #8a8a8a;
            letter-spacing: 0.3px;
            margin-top: 4px;
          }
          .allergy-disclaimer strong {
            color: #7f0000;
          }
          .empty-state {
            text-align: center;
            padding: 60px 20px;
            color: #999999;
            font-style: italic;
          }
        </style>
      </head>
      <body>
        ${pages.join('')}
      </body>
      </html>
    `;
  },

  // Open print dialog for the daily specials board
  openPrintDialog: (items: MenuItem[]) => {
    const html = dailySpecialsPrintService.generatePrintHTML(items);
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      throw new Error('Failed to open print window. Please allow popups for this site.');
    }

    printWindow.document.write(html);
    printWindow.document.close();

    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };

    setTimeout(() => {
      if (printWindow.document.readyState === 'complete') {
        printWindow.print();
      }
    }, 1000);
  },
};

export default printService;
